import React, { useState, useEffect } from 'react';
import { useReport } from '../hooks/useReport';
import { api } from '../api/client';
import styles from './ReportsPage.module.css';

/*
   Format a Date as YYYY-MM-DD using LOCAL date components.
   Avoids the UTC shift that toISOString() introduces for users east of
   UTC when the time-of-day is close to local midnight.
*/
function fmtLocal(d: Date): string {
	const vY = d.getFullYear(); // local year
	const vM = String(d.getMonth() + 1).padStart(2, '0'); // local month, 1-based
	const vD = String(d.getDate()).padStart(2, '0'); // local day-of-month
	return `${vY}-${vM}-${vD}`;
}
function todayStr() {
	return fmtLocal(new Date());
}
function monthStart() {
	const d = new Date();
	d.setDate(1);
	return fmtLocal(d);
}
function yearStart(y: number) {
	return `${y}-01-01`;
}
function yearEnd(y: number) {
	return `${y}-12-31`;
}

function fmtDays(days: number): string {
	return days.toFixed(2);
}

interface ProjectRow {
	client: string;
	name: string;
	team: string;
	category: string;
	reference: string;
	days: number;
}

type SortKey = 'days' | 'client' | 'name' | 'team' | 'category' | 'reference';

const COLUMNS: { key: SortKey; label: string; num?: boolean }[] = [
	{ key: 'days', label: 'JOURS', num: true },
	{ key: 'client', label: 'CLIENT' },
	{ key: 'name', label: 'NOM' },
	{ key: 'team', label: 'PÔLE' },
	{ key: 'category', label: 'CATÉGORIE' },
	{ key: 'reference', label: 'RÉFÉRENCE' },
];

export function ReportsPage() {
	const { downloadDetailed, downloadSynthesis } = useReport();
	const [from, setFrom] = useState(monthStart());
	const [to, setTo] = useState(todayStr());
	const [minDate, setMinDate] = useState<string | null>(null);
	const [projects, setProjects] = useState<ProjectRow[]>([]);
	const [sortKey, setSortKey] = useState<SortKey>('client');
	const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

	useEffect(() => {
		api.get<{ minDate: string | null }>('/reports/range')
			.then((r) => setMinDate(r.minDate))
			.catch(() => {});
	}, []);

	useEffect(() => {
		api.get<ProjectRow[]>(`/reports/projects?from=${from}&to=${to}`)
			.then(setProjects)
			.catch(() => setProjects([]));
	}, [from, to]);

	const thisYear = new Date().getFullYear();
	const prevYear = thisYear - 1;

	function setRange(f: string, t: string) {
		setFrom(f);
		setTo(t);
	}

	function handleSort(key: SortKey) {
		if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
		else {
			setSortKey(key);
			setSortDir('asc');
		}
	}

	const sorted = [...projects].sort((a, b) => {
		const av = a[sortKey];
		const bv = b[sortKey];
		const cmp =
			typeof av === 'number' && typeof bv === 'number'
				? av - bv
				: String(av).localeCompare(String(bv), 'fr', { sensitivity: 'base' });
		return sortDir === 'asc' ? cmp : -cmp;
	});

	const totalDays = projects.reduce((s, p) => s + p.days, 0);

	return (
		<div className={styles.page}>
			<h2 className={styles.title}>Exporter les rapports de temps</h2>
			<div className={styles.form}>
				<div className={styles.row}>
					<label className={styles.label}>
						Du
						<input
							type="date"
							value={from}
							onChange={(e) => setFrom(e.target.value)}
							className={styles.input}
						/>
					</label>
					<label className={styles.label}>
						Au
						<input
							type="date"
							value={to}
							onChange={(e) => setTo(e.target.value)}
							className={styles.input}
						/>
					</label>
				</div>

				<div className={styles.shortcuts}>
					<span className={styles.shortcutsLabel}>Raccourcis :</span>
					<button
						className={styles.shortcut}
						onClick={() => setRange(yearStart(thisYear), yearEnd(thisYear))}
					>
						{thisYear}
					</button>
					<button
						className={styles.shortcut}
						onClick={() => setRange(yearStart(prevYear), yearEnd(prevYear))}
					>
						{prevYear}
					</button>
					<button
						className={styles.shortcut}
						disabled={!minDate}
						onClick={() => minDate && setRange(minDate, todayStr())}
					>
						Toutes les données
					</button>
				</div>

				<div className={styles.row}>
					<div className={styles.reportCard}>
						<div className={styles.reportTitle}>Détaillé</div>
						<p className={styles.reportDesc}>
							Une ligne par jour / personne / projet.
							<br />
							Colonnes : date, heures, jours, personne, référence projet, nom du projet, catégorie.
						</p>
						<button className={styles.btn} onClick={() => downloadDetailed(from, to)}>
							Télécharger le CSV détaillé
						</button>
					</div>

					<div className={styles.reportCard}>
						<div className={styles.reportTitle}>Synthèse</div>
						<p className={styles.reportDesc}>
							Une ligne par projet, une colonne par personne.
							<br />
							Colonnes : référence, catégorie, nom du projet, puis total jours par personne.
						</p>
						<button className={styles.btn} onClick={() => downloadSynthesis(from, to)}>
							Télécharger le CSV de synthèse
						</button>
					</div>
				</div>
			</div>

			{projects.length > 0 && (
				<div className={styles.projectSection}>
					<div className={styles.projectHeader}>
						<span className={styles.projectTitle}>Projets sur la période</span>
						<span className={styles.projectTotal}>
							Total : <strong>{fmtDays(totalDays)} j</strong>
						</span>
					</div>
					<div className={styles.tableWrap}>
						<table className={styles.table}>
							<thead>
								<tr>
									{COLUMNS.map((col) => (
										<th
											key={col.key}
											className={col.num ? styles.thNum : styles.thSortable}
											onClick={() => handleSort(col.key)}
										>
											{col.label}
											{sortKey === col.key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{sorted.map((p, i) => (
									<tr key={i}>
										<td className={styles.tdNum}>{fmtDays(p.days)}</td>
										<td>{p.client}</td>
										<td>{p.name}</td>
										<td>{p.team}</td>
										<td>{p.category}</td>
										<td>{p.reference}</td>
									</tr>
								))}
							</tbody>
							<tfoot>
								<tr className={styles.tfootRow}>
									<td className={styles.tdNum}>
										<strong>{fmtDays(totalDays)}</strong>
									</td>
									<td colSpan={5}></td>
								</tr>
							</tfoot>
						</table>
					</div>
				</div>
			)}
		</div>
	);
}
